import { useState } from "react";
import { Button } from "react-bootstrap";
import { GiftFill, XLg } from "react-bootstrap-icons";
import { ValidateGiftcard } from "../../services";

type Props = {
  storeId: string;
  isDisabled?: boolean;
  onApply: (code: string) => void;
};

const formatGiftcardCode = (raw: string) => {
  const stripped = raw
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 12);
  return stripped.match(/.{1,3}/g)?.join("-") ?? "";
};

// Valid format: 4 groups of 3 alphanumeric chars separated by dashes
const isValidCode = (code: string) =>
  /^[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}$/.test(code);

const PayWithGiftcard: React.FC<Props> = ({
  storeId,
  isDisabled = false,
  onApply,
}) => {
  const [displayCode, setDisplayCode] = useState("");
  const [error, setError] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatGiftcardCode(e.target.value);
    setDisplayCode(formatted);
    if (error) setError("");
  };

  const handleRemove = () => {
    setAppliedCode("");
    setDisplayCode("");
    onApply("");
  };

  const handleApply = async () => {
    if (!displayCode.trim()) {
      setError("Please enter a gift card code.");
      return;
    }
    if (!isValidCode(displayCode)) {
      setError("Invalid code. Expected format: ABC-DEF-GHI-JKL");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const { status, message } = await ValidateGiftcard({
        store_id: storeId,
        code: displayCode,
      });

      if (status) {
        setAppliedCode(displayCode);
        onApply(displayCode);
      } else {
        setError(message);
      }
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="choose_payment_row1 d-flex justify-content-between align-items-center gap-3">
        <label>
          <span>
            <strong>Pay with Gift Card</strong> Redeem giftcard & get instant
            discount
          </span>
        </label>

        <div className="choose_payment_select">
          {appliedCode ? (
            <div
              className="d-flex align-items-center justify-content-between p-2 gap-4 rounded"
              style={{
                border: "1px solid #4caf50",
                background: "rgba(76,175,80,0.10)",
                animation: "gc-fadein 0.25s ease",
              }}
            >
              <span className="d-flex align-items-center gap-2">
                <style>{`@keyframes gc-fadein{from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}}`}</style>
                <GiftFill size={13} color="#2e7d32" />
                <span
                  style={{
                    fontFamily:
                      "ui-monospace, 'Cascadia Code', 'Courier New', monospace",
                    fontSize: 14,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    color: "#2e7d32",
                  }}
                >
                  {appliedCode}
                </span>
              </span>
              <button
                type="button"
                onClick={handleRemove}
                title="Remove gift card"
                style={{
                  background: "none",
                  border: "none",
                  padding: "0 0 0 4px",
                  cursor: "pointer",
                  lineHeight: 1,
                  color: "#777",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <XLg size={14} />
              </button>
            </div>
          ) : (
            <div className="input-group">
              <input
                type="text"
                className={`form-control${error ? " is-invalid" : ""}`}
                value={displayCode}
                onChange={handleCodeChange}
                placeholder="ABC-DEF-GHI-JKL"
                maxLength={15}
                disabled={isDisabled || isLoading}
              />
              <Button onClick={handleApply} disabled={isDisabled || isLoading}>
                {isLoading ? "Applying…" : "Apply"}
              </Button>
            </div>
          )}
          {error && <div className="invalid-feedback d-block">{error}</div>}
        </div>
      </div>
    </>
  );
};

export default PayWithGiftcard;
